import type { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { initStore } from '@/stores/store-utils';
import {
  IntegrationProfile,
  IntegrationProfileType,
} from '@/types/integration_profiles';

export const useIntegrationProfileStore = defineStore(
  'IntegrationProfileStore',
  () => {
    const integrationProfiles = ref<IntegrationProfile[]>([]);

    const { initError, ready, readySync, running, refresh } = initStore(
      async () => {
        const { data } = await crossbeamApi.GET('/v0.1/integration-profiles');
        if (data) integrationProfiles.value = data;
      },
    );

    refresh({ useCache: true });

    function getIntegrationProfileByType(
      integrationType: IntegrationProfileType,
    ) {
      return integrationProfiles.value.find(
        (profile) => profile.integration_type === integrationType,
      );
    }

    function getIntegrationProfilesByType(
      integrationType: IntegrationProfileType,
    ) {
      return integrationProfiles.value.filter(
        (profile) => profile.integration_type === integrationType,
      );
    }

    function getIntegrationProfileById(profileId: string) {
      return integrationProfiles.value.find(
        (profile) => profile.profile_id === profileId,
      );
    }

    function postIntegrationProfileSettings(
      payload: GetApiPayloadByUrl<'/v0.1/integration-profiles', 'post'>,
    ) {
      return crossbeamApi.POST('/v0.1/integration-profiles', {
        body: payload,
      });
    }

    async function putIntegrationProfileSettings(
      profileId: string,
      payload: GetApiPayloadByUrl<'/v0.1/integration-profiles/{id}', 'put'>,
    ) {
      const { data, error } = await crossbeamApi.PUT(
        '/v0.1/integration-profiles/{id}',
        {
          body: payload,
          params: {
            path: {
              id: profileId,
            },
          },
        },
      );
      if (error) throw error;
      return data;
    }

    function saveIntegrationProfileInStore(profile: IntegrationProfile) {
      const profileIndex = integrationProfiles.value.findIndex(
        (existing) => existing.profile_id === profile.profile_id,
      );
      if (profileIndex === -1) integrationProfiles.value.push(profile);
      else integrationProfiles.value.splice(profileIndex, 1, profile);
    }

    function deleteIntegrationProfileInStore(profileId: string) {
      const profileIndex = integrationProfiles.value.findIndex(
        (profile) => profile.profile_id === profileId,
      );
      if (profileIndex !== -1) {
        integrationProfiles.value.splice(profileIndex, 1);
      }
      return true;
    }

    return {
      error: initError,
      ready,
      readySync,
      running,
      refreshIntegrationProfileStore: refresh,
      integrationProfiles,
      getIntegrationProfileByType,
      getIntegrationProfilesByType,
      getIntegrationProfileById,
      postIntegrationProfileSettings,
      putIntegrationProfileSettings,
      saveIntegrationProfileInStore,
      deleteIntegrationProfileInStore,
    };
  },
);
